'use client';

import { Box, HStack } from '@terra/ui-styled-system';
import { IconButton, Text, Tooltip } from '@terra/ui-design-system';
import { Cancel01Icon, PinIcon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import {
  type Tab,
  FINDING_MAIN_CONTENT_TAB_TYPE,
} from '../../../store/layoutStore';
import { getMainContentTabIcon } from '../tabIcons';
import { useTargetType } from '../../../store/projectStore';
import { resolveOverviewTabTitle } from '../utils';
import { useFindingEditStore } from '../../../store/findingEditStore';

const NO_DRAG = { WebkitAppRegion: 'no-drag' } as React.CSSProperties;

type TabItemProps = {
  tab: Tab;
  isHovered: boolean;
  isDragging: boolean;
  isDragOver: boolean;
  isSelected: boolean;
  isGrouped?: boolean;
  onHover: (tabId: string | null) => void;
  onSelect: (tabId: string, event: React.MouseEvent) => void;
  onClose: (tabId: string) => void;
  onPin?: (tabId: string) => void;
  onContextMenu: (e: React.MouseEvent, tabId: string) => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  tabsCount: number;
  tabRef: (el: HTMLDivElement | null) => void;
};

export function TabItem({
  tab,
  isHovered,
  isDragging,
  isDragOver,
  isSelected,
  isGrouped = false,
  onHover,
  onSelect,
  onClose,
  onPin,
  onContextMenu,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  tabsCount,
  tabRef,
}: TabItemProps) {
  const t = useTranslations('layout');
  const targetType = useTargetType();
  const pinLabel = tab.isPinned ? t('unpinTab') : t('pinTab');
  const showPin = isHovered || tab.isPinned;
  const showClose = isHovered;
  const actionIconSize = isGrouped ? 14 : 16;
  const displayTitle = resolveOverviewTabTitle({
    tabId: tab.id,
    title: tab.title,
    tabType: tab.type,
    targetType,
    home: t('home'),
    targetOverview: t('targetOverview'),
  });
  const findingId =
    tab.type === FINDING_MAIN_CONTENT_TAB_TYPE
      ? tab.data?.findingId
      : undefined;
  const findingEditMode = useFindingEditStore((state) =>
    findingId ? state.getFindingEditState(findingId)?.mode : undefined,
  );
  const findingTabMode =
    tab.type === FINDING_MAIN_CONTENT_TAB_TYPE
      ? (findingEditMode ?? tab.data?.mode)
      : undefined;
  const background = tab.isActive
    ? 'bg.neutral.muted'
    : isSelected
      ? 'bg.neutral.muted'
      : 'bg.neutral.subtle';
  const foreground = tab.isActive
    ? 'fg.neutral.default'
    : isSelected
      ? 'fg.neutral.default'
      : 'fg.neutral.subtle';

  return (
    <Box
      ref={tabRef}
      position="relative"
      h={isGrouped ? 'full' : '8'}
      maxW="240px"
      minW="140px"
      flexBasis="240px"
      flexShrink={1}
      borderRadius="md"
      borderWidth="1px"
      borderStyle="solid"
      borderColor={isSelected ? 'border.neutral.emphasized' : 'transparent'}
      bg={background}
      color={foreground}
      cursor={isDragging ? 'grabbing' : 'grab'}
      userSelect="none"
      opacity={isDragging ? 0.5 : 1}
      transform={isDragOver ? 'scale(1.02)' : 'scale(1)'}
      transition="transform 0.2s, opacity 0.2s"
      overflow="hidden"
      style={NO_DRAG}
      draggable={true}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onMouseEnter={() => onHover(tab.id)}
      onMouseLeave={() => onHover(null)}
      onClick={(e) => onSelect(tab.id, e)}
      onContextMenu={(e) => onContextMenu(e, tab.id)}
      onMouseDown={(e) => {
        if (e.button === 1 && tabsCount > 1) {
          e.preventDefault();
          e.stopPropagation();
          onClose(tab.id);
        }
      }}
      _hover={{
        bg:
          tab.isActive || isSelected ? 'bg.neutral.muted' : 'bg.neutral.hover',
      }}
    >
      <Tooltip
        title={displayTitle}
        maxW="700px"
        allowOverflow
        positioning={{ placement: 'bottom', strategy: 'fixed' }}
      >
        <HStack
          h="full"
          pl="2.5"
          pr="14"
          gap="2"
          alignItems="center"
          overflow="hidden"
          w="full"
        >
          <Box
            color="inherit"
            flexShrink={0}
            display="flex"
            alignItems="center"
          >
            {getMainContentTabIcon(tab.type, { mode: findingTabMode })}
          </Box>
          <Text textStyle="body.strong" truncate color="inherit">
            {displayTitle}
          </Text>
        </HStack>
      </Tooltip>

      <HStack
        position="absolute"
        top={isGrouped ? 0 : '3px'}
        right="3px"
        h={isGrouped ? 'full' : undefined}
        gap="0"
        alignItems="center"
      >
        {onPin && (
          <Tooltip
            title={pinLabel}
            positioning={{ placement: 'bottom', strategy: 'fixed' }}
          >
            <IconButton
              variant="ghost"
              size="xs"
              aria-label={pinLabel}
              onClick={(e) => {
                e.stopPropagation();
                onPin(tab.id);
              }}
              color={tab.isPinned ? 'colorPalette.emphasized' : 'inherit'}
              _hover={{
                color: tab.isPinned
                  ? 'colorPalette.emphasized'
                  : 'fg.neutral.default',
                bg: 'bg.neutral.hover',
              }}
              opacity={showPin ? 1 : 0}
              visibility={showPin ? 'visible' : 'hidden'}
              pointerEvents={showPin ? 'auto' : 'none'}
              transition="opacity 0.1s ease, visibility 0.1s ease"
              w="6"
              h="6"
              minW="6"
              p="0"
              borderRadius="sm"
            >
              <PinIcon
                size={actionIconSize}
                fill={tab.isPinned ? 'currentColor' : 'none'}
              />
            </IconButton>
          </Tooltip>
        )}

        {tabsCount > 1 && (
          <Tooltip
            title={t('closeTab')}
            positioning={{ placement: 'bottom', strategy: 'fixed' }}
          >
            <IconButton
              variant="ghost"
              size="xs"
              aria-label={t('closeTab')}
              onClick={(e) => {
                e.stopPropagation();
                onClose(tab.id);
              }}
              color="inherit"
              _hover={{
                color: 'fg.neutral.default',
                bg: 'bg.neutral.hover',
              }}
              opacity={showClose ? 1 : 0}
              visibility={showClose ? 'visible' : 'hidden'}
              pointerEvents={showClose ? 'auto' : 'none'}
              transition="opacity 0.1s ease, visibility 0.1s ease"
              w="6"
              h="6"
              minW="6"
              p="0"
              borderRadius="sm"
            >
              <Cancel01Icon size={actionIconSize} />
            </IconButton>
          </Tooltip>
        )}
      </HStack>
    </Box>
  );
}
